import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismNumberInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { RecommendationStrategy } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { timeout } from 'lumen/utils/waiters';
import { localCopy } from 'tracked-toolbox';

import styles from './td-multi-room-dow.module.css';

type StrategyTableColumn = CustomEmberTableColumn & {
  id: number;
  isEditing: boolean;
  isSaveStrategyTaskRunning: boolean;
  onCopyToAllDays: (
    roomId: string,
    value: number | null,
    setting: 'minimum' | 'maximum',
  ) => void;
  onDayOfWeekValueChange: (
    dow: number,
    roomId: string,
    value: number | null,
    setting: 'minimum' | 'maximum',
  ) => void;
  saveAttempted: boolean;
  strategy: RecommendationStrategy;
};

type StrategyTableRow = CustomEmberTableRow & {
  roomId: string;
  roomName: string;
};

interface SettingsRecommendationsPriceRangeTdMultiRoomDowSignature {
  Args: {
    column: StrategyTableColumn;
    row: StrategyTableRow;
    value: {
      maximum: number | null;
      minimum: number | null;
    };
  };
}

export default class TdMultiRoomDow extends Component<SettingsRecommendationsPriceRangeTdMultiRoomDowSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  // Tracks the value currently in each input — changes are debounced before
  // committing to the strategy, so `@value` is stale until the user blurs or
  // the debounce fires. Copy-to-all-days uses these live values. `@localCopy`
  // re-seeds them whenever `@value` changes externally (e.g. another day's
  // copy-to-all-days) so they never go stale.
  @localCopy('args.value.maximum') declare liveMaxValue: number | null;
  @localCopy('args.value.minimum') declare liveMinValue: number | null;

  debouncedOnDayOfWeekValueChangeTask = task(
    { restartable: true },
    async (
      dowValue: number | null,
      setting: 'minimum' | 'maximum',
    ): Promise<void> => {
      await timeout('extra-long');
      this.setDayOfWeekValue(dowValue, setting);
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    const minimum =
      this.args.value.minimum !== null
        ? this.hotelAndMarketService.formatRate(this.args.value.minimum)
        : this.intl.t('pricing_assistant.recommendations.price_range.no_min');

    const maximum =
      this.args.value.maximum !== null
        ? this.hotelAndMarketService.formatRate(this.args.value.maximum)
        : this.intl.t('pricing_assistant.recommendations.price_range.no_max');

    return {
      minimum,
      maximum,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberInputsHaveErrors() {
    if (!this.args.column.saveAttempted) {
      return false;
    }

    const { strategy, id: columnId } = this.args.column;

    return strategy.multiRoomPriceRangeStrategyDayOfWeekWithErrors.some(
      (day) => day.dow === columnId && day.roomId === this.args.row.roomId,
    );
  }

  setDayOfWeekValue(
    dowValue: number | null,
    setting: 'minimum' | 'maximum',
  ): void {
    const { column, row } = this.args;

    this.args.column.onDayOfWeekValueChange(
      column.id,
      row.roomId,
      dowValue,
      setting,
    );
  }

  @action onCopyToAllDays(setting: 'minimum' | 'maximum'): void {
    const { column, row } = this.args;
    const liveValue =
      setting === 'minimum' ? this.liveMinValue : this.liveMaxValue;
    column.onCopyToAllDays(row.roomId, liveValue, setting);
  }

  @action onDayOfWeekMaximumFocusOut(event: Event): void {
    void this.debouncedOnDayOfWeekValueChangeTask.cancelAll();
    const target = event.target as HTMLInputElement;
    const value = target?.value ? parseInt(target.value, 10) : null;
    // Skip the commit when nothing changed — otherwise blurring an untouched
    // input rewrites the strategy and marks it dirty (activating Save).
    if (value === this.args.value.maximum) {
      return;
    }
    this.setDayOfWeekValue(value, 'maximum');
  }

  @action onDayOfWeekMaximumValueChange(dowValue: number | null): void {
    this.liveMaxValue = dowValue;
    void this.debouncedOnDayOfWeekValueChangeTask.perform(dowValue, 'maximum');
  }

  @action onDayOfWeekMinimumFocusOut(event: Event): void {
    void this.debouncedOnDayOfWeekValueChangeTask.cancelAll();
    const target = event.target as HTMLInputElement;
    const value = target?.value ? parseInt(target.value, 10) : null;
    // Skip the commit when nothing changed — otherwise blurring an untouched
    // input rewrites the strategy and marks it dirty (activating Save).
    if (value === this.args.value.minimum) {
      return;
    }
    this.setDayOfWeekValue(value, 'minimum');
  }

  @action onDayOfWeekMinimumValueChange(dowValue: number | null): void {
    this.liveMinValue = dowValue;
    void this.debouncedOnDayOfWeekValueChangeTask.perform(dowValue, 'minimum');
  }

  <template>
    <div
      class="flex flex-col py-100 {{if @column.isEditing 'gap-200' 'gap-400'}}"
    >
      {{#if @column.isEditing}}
        <div class={{styles.cell-wrapper}}>
          <div class={{styles.input-slot}}>
            <PrismNumberInput
              @disabled={{@column.isSaveStrategyTaskRunning}}
              @hasErrors={{this.numberInputsHaveErrors}}
              @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
              @isSmall={{true}}
              @isTouched={{true}}
              @min={{0}}
              @onFocusOut={{this.onDayOfWeekMinimumFocusOut}}
              @onValueChange={{this.onDayOfWeekMinimumValueChange}}
              @placeholder={{t "general.minimum.short"}}
              @removePadding={{true}}
              @value={{@value.minimum}}
            />
          </div>
          <span class={{styles.copy-wrapper}} data-copy-to-all-days>
            <PrismButton
              @buttonType="secondary"
              @icon="copy"
              @isDisabled={{@column.isSaveStrategyTaskRunning}}
              @onClick={{fn this.onCopyToAllDays "minimum"}}
              @size="small"
              aria-label={{t
                "pricing_assistant.recommendations.price_range.copy_to_all_days"
              }}
            />
            <PrismTooltip
              @side="top"
              @text={{t
                "pricing_assistant.recommendations.price_range.copy_to_all_days"
              }}
            />
          </span>
        </div>
        <div class={{styles.cell-wrapper}}>
          <div class={{styles.input-slot}}>
            <PrismNumberInput
              @disabled={{@column.isSaveStrategyTaskRunning}}
              @hasErrors={{this.numberInputsHaveErrors}}
              @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
              @isSmall={{true}}
              @isTouched={{true}}
              @min={{0}}
              @onFocusOut={{this.onDayOfWeekMaximumFocusOut}}
              @onValueChange={{this.onDayOfWeekMaximumValueChange}}
              @placeholder={{t "general.maximum.short"}}
              @removePadding={{true}}
              @value={{@value.maximum}}
            />
          </div>
          <span class={{styles.copy-wrapper}} data-copy-to-all-days>
            <PrismButton
              @buttonType="secondary"
              @icon="copy"
              @isDisabled={{@column.isSaveStrategyTaskRunning}}
              @onClick={{fn this.onCopyToAllDays "maximum"}}
              @size="small"
              aria-label={{t
                "pricing_assistant.recommendations.price_range.copy_to_all_days"
              }}
            />
            <PrismTooltip
              @side="top"
              @text={{t
                "pricing_assistant.recommendations.price_range.copy_to_all_days"
              }}
            />
          </span>
        </div>
      {{else}}
        <span>{{this.formattedValue.minimum}}</span>
        <span>{{this.formattedValue.maximum}}</span>
      {{/if}}
    </div>
  </template>
}
